// React & Third-party
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import 'dayjs/locale/pt'

// MUI
import { Box, MenuItem, Typography, Chip, SelectChangeEvent } from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

// Store & Types
import { Branch } from '../../types/branch'
import { useGetDriversQuery } from '../../store/api/timesheet/timesheetDriversApi'
import { useGetBranchesQuery } from '../../store/api/timesheet/timesheetBranchesApi'
import { addFilters, Driver, Filters, TimesheetStatus } from '../../store/slices/timesheet/timesheetDetailSlice'
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../store/timesheetStore'

// Utils & Hooks
import { calculateFiltersDates, filterById, filterByName } from '../../utils/filters'
import { useShouldSkipFilteredQuery } from '../../utils/hooks'
import { useGetTimesheetsTotalsQuery } from '../../utils/hooks/useGetTimesheetsTotalsQuery'

// Components
import { ChipLabel } from './ChipLabel/ChipLabel'
import CustomComboBox from '../v2/general/CustomComboBox/CustomComboBox'
import {
	tabPageTotalsBoxSx,
	tabPageTotalsTextStyle,
	tabPageChipSx,
	datePickerSx,
	datePickerCalendarSx,
	datePickerInputPropsStyle,
	menuItemDriverSx,
	menuItemDriverNameSx,
	menuItemInternalNumberSx,
} from './TableMotoristasFilters.styles'
import CustomSelect from '../v2/general/CustomSelect/CustomSelect'

// Module-level constants — never recreated on render
const onlyNumb = /^\d+$/

const DATE_PICKER_SLOT_PROPS = {
	textField: { inputProps: { style: datePickerInputPropsStyle } },
	popper: { sx: datePickerCalendarSx },
}

const formatPortugueseWeekDay = (date: dayjs.Dayjs): string => {
	const weekDayByIndex = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
	return weekDayByIndex[date.day()] ?? ''
}

const datesFilterBoxSx = { display: 'flex', alignItems: 'center', gap: '16px', marginTop: 4 }
const otherFiltersBoxSx = { paddingBlockStart: '1rem', display: 'grid', gap: 2 }

const filterBranchByName = (branches: Branch[], inputValue: string): Branch[] => {
	if (!inputValue) return branches
	const lowerInput = inputValue.toLowerCase()
	return branches.filter(branch => branch.companyName.toLowerCase().includes(lowerInput))
}

const filterBranchById = (branches: Branch[], inputValue: string): Branch[] => {
	if (!inputValue) return branches
	return branches.filter(branch => String(branch.companyDatabaseId).includes(inputValue))
}

interface TableMotoristaFiltersProps {
	showFilters: boolean // Keep component mounted to avoid triggering a new request
}

function TableMotoristaFilters({ showFilters }: TableMotoristaFiltersProps): React.JSX.Element {
	// Hooks: Redux, Router, Queries
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const [searchParams] = useSearchParams()

	// Narrow selector: only re-renders when filters change, not the whole timesheetDetails slice
	const timesheetFilters = useSelector((state: TimesheetStoreState) => state.timesheetDetails.filters)

	const shouldSkipFilteredQuery = useShouldSkipFilteredQuery()

	// Memoized values
	const queryParamsCount = useMemo(() => new URLSearchParams(searchParams).size, [searchParams])

	const driversQueryParams = useMemo(
		() => ({
			search: '',
			companyId: timesheetFilters?.branchId ? String(timesheetFilters.branchId) : '',
		}),
		[timesheetFilters?.branchId],
	)

	const initialStatusFilter = useMemo<TimesheetStatus[]>(
		() => ['Submitted', 'NotSubmitted', 'SubmittedWithoutChanges', 'AutoSubmitted', 'Rejected', 'Edited', 'Approved'],
		[],
	)

	// Data fetching
	const { data: drivers, isLoading } = useGetDriversQuery(driversQueryParams, { skip: shouldSkipFilteredQuery })
	const { data: branches, isLoading: isLoadingBranches } = useGetBranchesQuery(undefined)
	const { data: timesheetsTotals } = useGetTimesheetsTotalsQuery()

	// Local state
	const [driverValue, setDriverValue] = useState<null | Driver>(null)
	const [driverInputValue, setDriverInputValue] = useState('')
	const [branchValue, setBranchValue] = useState<null | Branch>(null)
	const [branchInputValue, setBranchInputValue] = useState('')
	const [statusFilter, setStatusFilter] = useState<TimesheetStatus[]>(initialStatusFilter)
	// Prevents the Redux sync effect from overriding local state after our own dispatch
	const skipStatusSync = useRef(false)

	// Syncs local statusFilter when Redux status changes externally (e.g. reset, URL params).
	// statusFilter is intentionally excluded from deps to avoid a render loop: having it as a dep
	// caused the effect to re-run after every local setStatusFilter call, creating an extra render
	// cycle and a brief window where the stale state could corrupt rapid-click interactions.
	// skipStatusSync prevents this from running when the change came from our own dispatch.
	// ReadyForEdit is filtered out because it has no corresponding UI option.
	useEffect(() => {
		if (skipStatusSync.current) {
			skipStatusSync.current = false
			return
		}
		if (Array.isArray(timesheetFilters.status)) {
			setStatusFilter(
				(timesheetFilters.status as TimesheetStatus[]).filter(s => s !== 'ReadyForEdit') as TimesheetStatus[],
			)
		}
	}, [timesheetFilters.status])

	useEffect(() => {
		const id = timesheetFilters.driverId
		if (id == null || id === 0) {
			setDriverValue(null)
			setDriverInputValue('')
			return
		}
		const driver = drivers?.find(d => d.driverId === id)
		if (!driver) return
		if (driverValue?.driverId !== id) {
			setDriverValue(driver)
			setDriverInputValue(driver.driverName)
		}
	}, [timesheetFilters.driverId, drivers, driverValue?.driverId])

	useEffect(() => {
		const id = timesheetFilters.branchId
		if (id == null || id === 0) {
			setBranchValue(null)
			setBranchInputValue('')
			return
		}
		const branch = branches?.find(b => b.companyDatabaseId === id)
		if (!branch) return
		if (branchValue?.companyDatabaseId !== id) {
			setBranchValue(branch)
			setBranchInputValue(branch.companyName)
		}
	}, [timesheetFilters.branchId, branches, branchValue?.companyDatabaseId])

	// filters array fully inside useMemo — deps are the real data sources
	const filters = useMemo(
		() => [
			{
				value: 'Approved',
				label: `Aprovadas (${timesheetsTotals?.approved || 0})`,
				checked: timesheetFilters.status?.includes('Approved'),
				disabled: false,
			},
			{
				value: 'Submitted',
				label: `Submetidas (${timesheetsTotals?.submitted || 0})`,
				checked: timesheetFilters.status?.includes('Submitted'),
				disabled: false,
			},
			{
				value: 'NotSubmitted',
				label: `Não Submetidas (${timesheetsTotals?.notSubmitted || 0})`,
				checked: timesheetFilters.status?.includes('NotSubmitted'),
				disabled: false,
			},
			{
				value: 'SubmittedWithoutChanges',
				label: `Submetidas Sem Alterações (${timesheetsTotals?.submittedWithoutChanges || 0})`,
				checked: timesheetFilters.status?.includes('SubmittedWithoutChanges'),
				disabled: false,
			},
			{
				value: 'AutoSubmitted',
				label: `Submetidas Automaticamente (${timesheetsTotals?.autoSubmitted || 0})`,
				checked: timesheetFilters.status?.includes('AutoSubmitted'),
				disabled: false,
			},
			{
				value: 'Rejected',
				label: `Recusadas (${timesheetsTotals?.rejected || 0})`,
				checked: timesheetFilters.status?.includes('Rejected'),
				disabled: false,
			},
			{
				value: 'Edited',
				label: `Editadas (${timesheetsTotals?.edited || 0})`,
				checked: timesheetFilters.status?.includes('Edited'),
				disabled: false,
			},
		],
		[timesheetsTotals, timesheetFilters.status],
	)

	// Stable event handlers
	const handleStatusFilterChange = useCallback(
		(event: SelectChangeEvent<string | string[]>) => {
			const value = event.target.value

			const enabledStatus = filters.filter(f => !f.disabled).map(f => f.value)
			const filteredValue =
				typeof value === 'string'
					? value
							.split(',')
							.map(v => v.trim())
							.filter(v => enabledStatus.includes(v))
					: value.filter(v => enabledStatus.includes(v))

			let expandedStatus: TimesheetStatus[] = filteredValue as TimesheetStatus[]
			if (filteredValue.includes('Edited')) {
				const set = new Set<string>(expandedStatus)
				set.add('ReadyForEdit')
				set.add('Edited')
				expandedStatus = Array.from(set) as TimesheetStatus[]
			}

			// Use filteredValue (not expandedStatus) so the UI accurately reflects what the user
			// selected. Previously this was set to ['Edited'] when Edited was included, which caused
			// MUI Select to show only Edited as checked — making rapid clicks add instead of remove
			// other items, and clicking Edited itself would produce value=[] unchecking everything.
			setStatusFilter(filteredValue as TimesheetStatus[])

			// Tell the sync effect to skip the next Redux update since we're dispatching it ourselves.
			// Without this, the effect would call setStatusFilter again with the Redux value,
			// triggering a redundant render after every status change.
			skipStatusSync.current = true
			dispatch(addFilters({ ...timesheetFilters, status: expandedStatus }))
		},
		[filters, timesheetFilters, dispatch],
	)

	const handleFilterReset = useCallback(() => {
		const resetFilters: Filters = {
			startDate: String(calculateFiltersDates('StartDate')),
			endDate: String(calculateFiltersDates('EndDate')),
			currentPage: 1,
			pageSize: 20,
			ascending: true,
			status: [
				'Submitted',
				'NotSubmitted',
				'SubmittedWithoutChanges',
				'AutoSubmitted',
				'Rejected',
				'Edited',
				'ReadyForEdit',
				'Approved',
			],
			orderByColumn: 'TimeSheetDate',
			driverId: undefined,
			branchId: undefined,
		}
		dispatch(addFilters(resetFilters))
		setDriverValue(null)
		setDriverInputValue('')
		setBranchValue(null)
		setBranchInputValue('')
	}, [dispatch])

	const handleStartDateChange = useCallback(
		(newValue: dayjs.Dayjs | null) => {
			if (!newValue) return
			const newStart = dayjs(newValue)
			const currentEnd = dayjs(timesheetFilters.endDate)
			const nextEndDate = currentEnd.isValid() && newStart.isAfter(currentEnd) ? newStart : currentEnd
			dispatch(
				addFilters({
					...timesheetFilters,
					startDate: String(newStart.format('YYYY-MM-DD')),
					endDate: String(nextEndDate.format('YYYY-MM-DD')),
				}),
			)
		},
		[timesheetFilters, dispatch],
	)

	const handleEndDateChange = useCallback(
		(newValue: dayjs.Dayjs | null) => {
			if (!newValue) return
			const newEnd = dayjs(newValue)
			const currentStart = dayjs(timesheetFilters.startDate)
			const nextStartDate = currentStart.isValid() && newEnd.isBefore(currentStart) ? newEnd : currentStart
			dispatch(
				addFilters({
					...timesheetFilters,
					startDate: String(nextStartDate.format('YYYY-MM-DD')),
					endDate: String(newEnd.format('YYYY-MM-DD')),
				}),
			)
		},
		[timesheetFilters, dispatch],
	)

	const handleBranchChange = useCallback(
		(_event: React.SyntheticEvent, newValue: null | Branch) => {
			setBranchValue(newValue)
			setBranchInputValue(newValue ? newValue.companyName : '')
			dispatch(addFilters({ branchId: newValue ? newValue.companyDatabaseId : undefined }))
		},
		[dispatch],
	)

	const handleBranchInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string) => {
		setBranchInputValue(newInputValue)
	}, [])

	const handleDriverChange = useCallback(
		(_event: React.SyntheticEvent, newValue: null | Driver) => {
			setDriverValue(newValue)
			setDriverInputValue(newValue ? newValue.driverName : '')
			dispatch(addFilters({ driverId: newValue ? newValue.driverId : undefined }))
		},
		[dispatch],
	)

	const handleDriverInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string) => {
		setDriverInputValue(newInputValue)
	}, [])

	const filterBranchOptions = useCallback(
		(options: Branch[]) =>
			onlyNumb.test(branchInputValue)
				? filterBranchById(options, branchInputValue)
				: filterBranchByName(options, branchInputValue),
		[branchInputValue],
	)

	const filterDriverOptions = useCallback(
		(options: Driver[]) =>
			onlyNumb.test(driverInputValue) ? filterById(options, driverInputValue) : filterByName(options, driverInputValue),
		[driverInputValue],
	)

	const getDriverOptionLabel = useCallback(
		(option: Driver) => (onlyNumb.test(driverInputValue) ? String(option.internalNumber) : option.driverName),
		[driverInputValue],
	)

	const getBranchOptionLabel = useCallback((option: Branch) => option.companyName, [])

	const isBranchOptionEqual = useCallback(
		(option: Branch, v: Branch) => option.companyDatabaseId === v?.companyDatabaseId,
		[],
	)

	const isDriverOptionEqual = useCallback((option: Driver, v: Driver) => option.driverId === v?.driverId, [])

	const renderDriverOption = useCallback(
		(props: React.HTMLAttributes<HTMLLIElement>, item: Driver) => (
			<MenuItem
				sx={menuItemDriverSx}
				value={item.driverId}
				{...props}
				key={item.driverId}
			>
				<Typography sx={menuItemDriverNameSx}>{item.driverName}</Typography>
				<Typography
					variant='body2'
					sx={menuItemInternalNumberSx}
				>
					{item.internalNumber}
				</Typography>
			</MenuItem>
		),
		[],
	)

	return (
		<Box id='timesheet-filters-box'>
			<Box sx={tabPageTotalsBoxSx}>
				{timesheetsTotals && (
					<span style={tabPageTotalsTextStyle}>
						{timesheetsTotals.totalFiltered} Folha
						{timesheetsTotals.totalFiltered !== 1 && 's'} de Ponto
					</span>
				)}
				<Chip
					label={<ChipLabel filterCount={queryParamsCount} />}
					variant='outlined'
					color='info'
					onDelete={handleFilterReset}
					onClick={handleFilterReset}
					sx={tabPageChipSx}
				/>
			</Box>
			{/* Date filters */}
			{showFilters && (
				<LocalizationProvider
					adapterLocale='pt'
					dateAdapter={AdapterDayjs}
				>
					<Box sx={datesFilterBoxSx}>
						<DatePicker
							sx={datePickerSx}
							label='Data Inicial'
							value={dayjs(timesheetFilters.startDate)}
							format='DD-MM-YYYY'
							dayOfWeekFormatter={formatPortugueseWeekDay}
							onChange={handleStartDateChange}
							openTo='day'
							showDaysOutsideCurrentMonth
							slotProps={DATE_PICKER_SLOT_PROPS}
						/>
						<h5>{'—'}</h5>
						<DatePicker
							sx={datePickerSx}
							label='Data Final'
							value={dayjs(timesheetFilters.endDate)}
							format='DD-MM-YYYY'
							dayOfWeekFormatter={formatPortugueseWeekDay}
							onChange={handleEndDateChange}
							showDaysOutsideCurrentMonth
							slotProps={DATE_PICKER_SLOT_PROPS}
						/>
					</Box>
				</LocalizationProvider>
			)}
			{showFilters && (
				<Box sx={otherFiltersBoxSx}>
					{/* Branch lookup */}
					{branches && branches.length > 1 && (
						<CustomComboBox
							label='Filial'
							size='small'
							tabIndex={-1}
							value={branchValue}
							onChange={handleBranchChange}
							inputValue={branchInputValue}
							onInputChange={handleBranchInputChange}
							options={isLoadingBranches || !branches ? [] : branches}
							isOptionEqualToValue={isBranchOptionEqual}
							filterOptions={filterBranchOptions}
							getOptionLabel={getBranchOptionLabel}
						/>
					)}
					{/* Driver lookup */}
					<CustomComboBox
						label='Motorista'
						size='small'
						tabIndex={-1}
						value={driverValue}
						onChange={handleDriverChange}
						inputValue={driverInputValue}
						onInputChange={handleDriverInputChange}
						options={isLoading || !drivers ? [] : drivers}
						isOptionEqualToValue={isDriverOptionEqual}
						filterOptions={filterDriverOptions}
						getOptionLabel={getDriverOptionLabel}
						renderOption={renderDriverOption}
					/>
					{/* Submition status filters */}
					<CustomSelect
						multiple
						withIndicators
						aria-label='Filtro de Estado da Folha de Ponto'
						label='Estado'
						value={statusFilter as string[]}
						changeHandler={handleStatusFilterChange}
						options={filters.map(option => ({
							value: option.value,
							label: option.label as string,
						}))}
					/>
				</Box>
			)}
		</Box>
	)
}

export default TableMotoristaFilters
